<template>
  <footer
      ref="root"
      class="app-footer d-print-none"
  >
    <div class="app-footer__content container-fluid">
      <AppFooterBusinessHours/>
      <AppFooterContact/>
      <AppFooterPhone/>
      <AppFooterCertificates/>
    </div>
    <AppFooterSocials/>
    <AppFooterSub
        :items="menuSub || []"
    />
  </footer>
</template>

<script lang="ts" setup>
import AppFooterBusinessHours from '@/components/footer/AppFooterBusinessHours.vue';
import AppFooterCertificates from '@/components/footer/AppFooterCertificates.vue';
import AppFooterContact from '@/components/footer/AppFooterContact.vue';
import AppFooterPhone from '@/components/footer/AppFooterPhone.vue';
import AppFooterSub from '@/components/footer/AppFooterSub.vue';
import AppFooterSocials from '@/components/footer/AppFooterSocials.vue';
import {
  computed, onBeforeMount, ref,
} from 'vue';
import { useStore } from 'vuex';

const store = useStore();
const root = ref<InstanceType<typeof HTMLElement>>();

onBeforeMount(() => {
  store.dispatch('menus/fetchBySlug', 'footer');
  store.dispatch('menus/fetchBySlug', 'footer-sub');
});

const menuSub = computed(() => store.getters['menus/menu']('footer-sub'));
</script>

<style lang="scss">
@import "src/scss/component-helpers";

.app-footer {
  position: relative;
  z-index: 1;
  background-color: $body-bg-secondary;
  line-height: 1rem;

  &__content {
    display: grid;
    width: 100%;
    grid-auto-rows: auto;
    grid-template-columns: 100%;
    justify-items: start;
    padding: 1.25rem 0 1rem;
    gap: 1.25rem;

    @media (min-width: map_get($grid-breakpoints, 'sm')) {
      grid-template-columns: 1fr max-content;
    }

    @media (min-width: map_get($grid-breakpoints, 'lg')) {
      padding: 1.5625rem 0 1.25rem;
      grid-template-columns: 1fr 1fr 1fr;
    }

    @media (min-width: map_get($grid-breakpoints, 'xxl')) {
      grid-template-columns: auto auto auto auto;
      gap: 1.25rem;
      justify-content: space-between;
    }

    .app-footer {
      &__business-hours {
        grid-column: 1 / 2;
        grid-row: 1 / 2;

        @media (min-width: map_get($grid-breakpoints, 'sm')) {
          grid-row: 1 / 2;
        }

        @media (min-width: map_get($grid-breakpoints, 'lg')) {
          grid-row: 1 / 2;
          grid-column: 1 / 2;
        }

        @media (min-width: map_get($grid-breakpoints, 'xxl')) {
          grid-row: 1 / 2;
          grid-column: 1 / 2;
        }
      }

      &__contact {
        grid-column: 1 / 2;
        grid-row: 2 / 3;

        @media (min-width: map_get($grid-breakpoints, 'sm')) {
          grid-row: 2 / 3;
        }

        @media (min-width: map_get($grid-breakpoints, 'lg')) {
          grid-row: 1 / 2;
          grid-column: 2 / 3;
        }
      }

      &__phone {
        grid-column: 1 / 2;
        grid-row: 3 / 4;

        @media (min-width: map_get($grid-breakpoints, 'sm')) {
          grid-row: 3 / 4;
        }

        @media (min-width: map_get($grid-breakpoints, 'lg')) {
          grid-row: 1 / 2;
          grid-column: 3 / 4;
        }
      }

      &__certificates {
        grid-column: 1 / 2;
        grid-row: 4 / 5;

        @media (min-width: map_get($grid-breakpoints, 'sm')) {
          grid-row: 1 / 4;
          grid-column: 2 / 3;
          align-self: start;
        }

        @media (min-width: map_get($grid-breakpoints, 'lg')) {
          grid-row: 1 / 3;
          grid-column: 3 / 4;
        }

        @media (min-width: map_get($grid-breakpoints, 'lg')) {
          grid-row: 2 / 3;
          grid-column: 1 / 4;
        }

        @media (min-width: map_get($grid-breakpoints, 'xxl')) {
          grid-row: 1 / 2;
          grid-column: 4 / 5;
        }
      }
    }
  }

  &__title {
    font-size: .75rem;
    margin-bottom: .625rem;
    font-weight: 700;

    @media (min-width: map_get($grid-breakpoints, 'lg')) {
      font-size: .875rem;
      margin-bottom: .75rem;
    }
  }

  .icon-row {
    display: grid;
    grid-gap: 10px;
    grid-template-columns: auto 1fr;
    align-items: center;
    gap: 12px;

    @media (min-width: map_get($grid-breakpoints, 'lg')) {
      gap: 1.25rem;
    }

    > div {
      &:first-child {
        grid-column: 1 / 2;
        font-size: 1rem;
        color: $primary;
        line-height: 0;

        @media (min-width: map_get($grid-breakpoints, 'lg')) {
          font-size: 1.125rem;
        }
      }

      &:last-child {
        grid-column: 2 / 3;
        font-size: .75rem;

        a, a:hover, a:visited {
          text-decoration: none;
          color: $body-color;
        }

        @media (min-width: map_get($grid-breakpoints, 'lg')) {
          font-size: .875rem;
        }
      }
    }

    & + .icon-row {
      margin-top: .625rem;

      @media (min-width: map_get($grid-breakpoints, 'lg')) {
        margin-top: 1.125rem;
      }
    }
  }

  table {
    tr {
      td {
        font-size: .75rem;
        padding-bottom: .625rem;

        @media (min-width: map_get($grid-breakpoints, 'lg')) {
          font-size: .875rem;
        }

        &:last-child {
          padding-left: .9375rem;

          @media (min-width: map_get($grid-breakpoints, 'lg')) {
            padding-left: 1.5625rem;
          }
        }
      }

      &:last-child {
        td {
          padding-bottom: 0;
        }
      }
    }
  }
}
</style>
